// Vulnerability Findings: paginated table of monitor_vuln_findings from the
// monitor beacon's NVD enrichment, nuclei runs, and ZAP baseline scans.
// Bulk select + resolve action with reason.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bug,
  Check,
  ExternalLink,
  Flame,
  Loader2,
  RefreshCw,
  Server,
  ShieldCheck,
} from 'lucide-react';
import { monitorAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../common/PageHeader';
import ConfirmModal from '../common/ConfirmModal';
import { formatRelativeTime, formatAbsoluteDate } from '../../utils/time';

const PAGE_SIZE = 50;

const SEVERITY_OPTIONS = [
  { label: 'Critical', value: 'critical', dotClass: 'bg-red-500' },
  { label: 'High', value: 'high', dotClass: 'bg-orange-500' },
  { label: 'Medium', value: 'medium', dotClass: 'bg-yellow-500' },
  { label: 'Low', value: 'low', dotClass: 'bg-blue-500' },
  { label: 'Info', value: 'info', dotClass: 'bg-slate-500' },
];

const SOURCE_OPTIONS = [
  { label: 'NVD', value: 'nvd' },
  { label: 'Nuclei', value: 'nuclei' },
  { label: 'ZAP', value: 'zap' },
];

const SEVERITY_BADGE = {
  critical: 'bg-red-100 text-red-700 border border-red-300',
  high: 'bg-orange-100 text-orange-700 border border-orange-300',
  medium: 'bg-yellow-100 text-yellow-700 border border-yellow-300',
  low: 'bg-blue-100 text-blue-700 border border-blue-300',
  info: 'bg-slate-100 text-slate-700 border border-slate-300',
};

const SOURCE_BADGE = {
  nvd: 'bg-purple-100 text-purple-700 border border-purple-300',
  nuclei: 'bg-cyan-100 text-cyan-700 border border-cyan-300',
  zap: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
};

export default function VulnerabilityFindings() {
  const { isOperator } = useAuth(); // Red/Blue Teamer can resolve; Wasabian read-only
  const [findings, setFindings] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterSeverities, setFilterSeverities] = useState(new Set());
  const [filterSources, setFilterSources] = useState(new Set());
  const [includeResolved, setIncludeResolved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [resolveModal, setResolveModal] = useState({ open: false });
  const [resolveReason, setResolveReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchFindings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
        include_resolved: includeResolved,
      };
      if (filterSeverities.size > 0) params.severity = Array.from(filterSeverities);
      if (filterSources.size > 0) params.source = Array.from(filterSources);
      const resp = await monitorAPI.listFindings(params);
      setFindings(resp.data.findings || []);
      setTotal(resp.data.total || 0);
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [page, filterSeverities, filterSources, includeResolved]);

  useEffect(() => {
    fetchFindings();
  }, [fetchFindings]);

  const toggleSeverity = (sev) => {
    setPage(0);
    const next = new Set(filterSeverities);
    if (next.has(sev)) next.delete(sev);
    else next.add(sev);
    setFilterSeverities(next);
  };

  const toggleSource = (src) => {
    setPage(0);
    const next = new Set(filterSources);
    if (next.has(src)) next.delete(src);
    else next.add(src);
    setFilterSources(next);
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const allOpenIds = useMemo(
    () => findings.filter((f) => !f.resolved_at).map((f) => f.id),
    [findings]
  );

  const toggleSelectAll = () => {
    if (selected.size === allOpenIds.length && allOpenIds.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allOpenIds));
    }
  };

  const handleBulkResolve = async () => {
    setBusy(true);
    try {
      for (const id of selected) {
        await monitorAPI.resolveFinding(id, resolveReason.trim());
      }
      setResolveModal({ open: false });
      setResolveReason('');
      setSelected(new Set());
      await fetchFindings();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vulnerability Findings"
        subtitle="CVE matches, nuclei templates, and ZAP alerts from monitor beacons. Resolve bulk-style with a reason."
        icon={Bug}
        accent="purple"
        lastRefresh={lastRefresh?.toLocaleTimeString()}
        actions={
          <div className="flex items-center gap-2">
            {selected.size > 0 && isOperator && (
              <button
                onClick={() => setResolveModal({ open: true })}
                className="px-3 py-1.5 text-xs rounded border border-wasabi-300 bg-wasabi-100 text-wasabi-700 hover:bg-wasabi-100 transition-colors flex items-center gap-1.5"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Resolve {selected.size}
              </button>
            )}
            <button
              onClick={fetchFindings}
              className="px-3 py-1.5 text-xs rounded border border-shinobi-purple/40 bg-shinobi-purple/10 text-shinobi-purple hover:bg-shinobi-purple/20 transition-colors flex items-center gap-1.5"
              title="Refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
          </div>
        }
      />

      <div className="px-6 space-y-4">
        {/* Filters */}
        <div className="cyber-card p-4 space-y-3">
          <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
            <span className="text-xs text-slate-500 uppercase tracking-wider mr-1">Severity:</span>
            {SEVERITY_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => toggleSeverity(o.value)}
                className={`px-2 py-1 text-xs rounded border transition-colors flex items-center gap-1.5 ${
                  filterSeverities.has(o.value)
                    ? 'border-shinobi-purple bg-shinobi-purple/20 text-slate-900'
                    : 'border-slate-200 text-slate-500 hover:border-gray-500'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${o.dotClass}`} />
                {o.label}
              </button>
            ))}
          </div>
          <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
            <span className="text-xs text-slate-500 uppercase tracking-wider mr-1">Source:</span>
            {SOURCE_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => toggleSource(o.value)}
                className={`px-2 py-1 text-xs rounded border transition-colors ${
                  filterSources.has(o.value)
                    ? 'border-shinobi-purple bg-shinobi-purple/20 text-slate-900'
                    : 'border-slate-200 text-slate-500 hover:border-gray-500'
                }`}
              >
                {o.label}
              </button>
            ))}
            <label className="flex items-center gap-1.5 text-xs text-slate-500 ml-auto cursor-pointer">
              <input
                type="checkbox"
                checked={includeResolved}
                onChange={(e) => {
                  setPage(0);
                  setIncludeResolved(e.target.checked);
                }}
                className="accent-shinobi-purple"
              />
              Include resolved
            </label>
          </div>
        </div>

        {/* Table */}
        <div className="cyber-card overflow-hidden">
          {loading ? (
            <div className="p-8 text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading findings...
            </div>
          ) : error ? (
            <div className="p-8 text-red-600 text-sm">Error: {error}</div>
          ) : findings.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-cyber-200/40 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={allOpenIds.length > 0 && selected.size === allOpenIds.length}
                        onChange={toggleSelectAll}
                        className="accent-shinobi-purple"
                        title="Select all open"
                      />
                    </th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Severity</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Threat</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Source</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Vault</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Asset</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">ID</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Title</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">First seen</th>
                    <th className="text-left px-3 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">State</th>
                  </tr>
                </thead>
                <tbody>
                  {findings.map((f) => (
                    <FindingRow
                      key={f.id}
                      finding={f}
                      selected={selected.has(f.id)}
                      onToggleSelect={() => toggleSelect(f.id)}
                      expanded={expanded === f.id}
                      onToggleExpand={() => setExpanded(expanded === f.id ? null : f.id)}
                    />
                  ))}
                </tbody>
              </table>
              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-xs text-slate-500">
                  <span>
                    Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(Math.max(0, page - 1))}
                      disabled={page === 0}
                      className="px-2 py-1 rounded border border-slate-200 hover:bg-cyber-200/40 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Prev
                    </button>
                    <span className="px-2">Page {page + 1} / {totalPages}</span>
                    <button
                      onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                      disabled={page >= totalPages - 1}
                      className="px-2 py-1 rounded border border-slate-200 hover:bg-cyber-200/40 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={resolveModal.open}
        onClose={() => setResolveModal({ open: false })}
        onConfirm={handleBulkResolve}
        title={`Resolve ${selected.size} finding${selected.size === 1 ? '' : 's'}`}
        confirmText={busy ? 'Resolving...' : 'Resolve'}
        cancelText="Cancel"
        variant="warning"
        message={
          <div className="space-y-3 text-sm">
            <p className="text-slate-700">
              Mark the selected findings as resolved. They stay queryable via the
              Include resolved toggle. If the same vulnerability is re-observed by
              a future scan, the finding will reopen automatically.
            </p>
            <textarea
              value={resolveReason}
              onChange={(e) => setResolveReason(e.target.value)}
              placeholder="Reason (required) - e.g. patched, false positive, accepted risk"
              rows={3}
              className="w-full bg-cyber-300 border border-slate-200 rounded px-3 py-2 text-sm text-slate-900 focus:outline-none focus:border-shinobi-purple"
              autoFocus
            />
          </div>
        }
      />
    </div>
  );
}

function FindingRow({ finding, selected, onToggleSelect, expanded, onToggleExpand }) {
  const isResolved = !!finding.resolved_at;
  const idLabel = finding.cve_id || finding.template_id || '—';
  const idHref = finding.cve_id
    ? `https://nvd.nist.gov/vuln/detail/${finding.cve_id}`
    : null;

  return (
    <>
      <tr className={`border-t border-slate-200 ${isResolved ? 'opacity-60' : ''}`}>
        <td className="px-3 py-3 align-top">
          {!isResolved && (
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelect}
              className="accent-shinobi-purple"
            />
          )}
        </td>
        <td className="px-3 py-3 align-top">
          <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded ${SEVERITY_BADGE[finding.severity] || SEVERITY_BADGE.info}`}>
            {finding.severity}
          </span>
        </td>
        <td className="px-3 py-3 align-top">
          <div className="flex items-center gap-1 flex-wrap">
            {finding.kev && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-red-100 text-red-700 border border-red-300" title="CISA Known Exploited Vulnerability">
                <Flame className="w-3 h-3" /> KEV
              </span>
            )}
            {finding.epss_score > 0 && (
              <span className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-purple-100 text-purple-700 border border-purple-300" title="FIRST.org EPSS exploit probability">
                {Math.round(finding.epss_score * 100)}%
              </span>
            )}
            {!finding.kev && !(finding.epss_score > 0) && (
              <span className="text-gray-600 text-xs">—</span>
            )}
          </div>
        </td>
        <td className="px-3 py-3 align-top">
          <span className={`px-2 py-0.5 text-[10px] font-mono rounded ${SOURCE_BADGE[finding.source] || ''}`}>
            {finding.source}
          </span>
        </td>
        <td className="px-3 py-3 align-top text-xs text-slate-700">
          {finding.vault ? (
            <span className="inline-flex items-center gap-1">
              <Server className="w-3 h-3 text-gray-500" />
              {finding.vault}
            </span>
          ) : (
            <span className="text-gray-600">Unassigned</span>
          )}
        </td>
        <td className="px-3 py-3 align-top text-xs font-mono text-slate-700">
          {finding.asset_ip || <span className="text-gray-600">—</span>}
        </td>
        <td className="px-3 py-3 align-top text-sm font-mono text-slate-700">
          {idHref ? (
            <a
              href={idHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-shinobi-purple hover:underline flex items-center gap-1"
            >
              {idLabel}
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            idLabel
          )}
        </td>
        <td className="px-3 py-3 align-top text-sm text-slate-800">
          <button
            onClick={onToggleExpand}
            className="text-left hover:text-slate-900 transition-colors"
          >
            {finding.title}
          </button>
        </td>
        <td className="px-3 py-3 align-top text-xs text-slate-500" title={formatAbsoluteDate(finding.first_seen_at)}>
          {formatRelativeTime(finding.first_seen_at)}
        </td>
        <td className="px-3 py-3 align-top text-xs">
          {isResolved ? (
            <span className="flex items-center gap-1 text-shinobi-green">
              <Check className="w-3 h-3" /> Resolved
            </span>
          ) : (
            <span className="text-orange-700">Open</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-slate-200 bg-cyber-200/20">
          <td colSpan={10} className="px-6 py-3">
            <div className="space-y-2 text-sm">
              {finding.description && (
                <p className="text-slate-700 whitespace-pre-wrap">{finding.description}</p>
              )}
              {finding.evidence && (
                <details className="text-xs">
                  <summary className="text-slate-500 cursor-pointer hover:text-slate-800">
                    Evidence
                  </summary>
                  <pre className="mt-1 p-2 bg-cyber-50 rounded border border-slate-200 text-slate-700 overflow-x-auto">
                    {JSON.stringify(finding.evidence, null, 2)}
                  </pre>
                </details>
              )}
              {isResolved && finding.resolved_reason && (
                <p className="text-xs text-slate-500">
                  Resolved {formatRelativeTime(finding.resolved_at)} - reason: {finding.resolved_reason}
                </p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function EmptyState() {
  return (
    <div className="p-10 text-center">
      <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-600" />
      <p className="text-slate-700 text-sm mb-1">No findings match these filters.</p>
      <p className="text-gray-500 text-xs">
        Monitor beacons populate findings as they enrich discovered services via
        NVD, run nuclei, and complete ZAP baseline scans. Adjust filters or check
        back after a discovery cycle.
      </p>
    </div>
  );
}
